/**
 * BLOB-based vector store using SQLite.
 * Stores embeddings as Float32Array buffers, searches via brute-force cosine similarity.
 * Fine for codebases with <10K symbols.
 */
import type Database from 'better-sqlite3';
import type { VectorStore } from './interfaces.js';
import { VEC_TABLE_NAME, Vec0Index } from './vec-extension.js';

/**
 * Fraction of the index that may be vec0 orphans before reconciliation
 * rebuilds the accelerator from scratch (DROP + full backfill) instead of
 * deleting orphan rows one by one (GH#1447). Row-by-row DELETEs leave dead
 * weight in vec0 shadow chunks; past this ratio a rebuild is cheaper and
 * compacts the chunks. The absolute floor keeps tiny transient drift (a few
 * rows mid-reindex) on the cheap prune path.
 */
export const VEC_REBUILD_ORPHAN_RATIO = 0.2;
export const VEC_REBUILD_MIN_ORPHANS = 1000;

/** Pure rebuild-vs-prune decision for the vec0 reconciler (GH#1447). */
export function shouldRebuildVecIndex(orphans: number, live: number): boolean {
  if (orphans < VEC_REBUILD_MIN_ORPHANS) return false;
  return orphans / (live + orphans) >= VEC_REBUILD_ORPHAN_RATIO;
}

const CREATE_TABLE = `
CREATE TABLE IF NOT EXISTS symbol_embeddings (
  symbol_id INTEGER PRIMARY KEY REFERENCES symbols(id) ON DELETE CASCADE,
  embedding BLOB NOT NULL
);
CREATE TABLE IF NOT EXISTS embedding_meta (
  key   TEXT PRIMARY KEY,
  value TEXT NOT NULL
);`;

export class DimensionMismatchError extends Error {
  constructor(expected: number, got: number) {
    super(
      `Vector dimension mismatch: expected ${expected}, got ${got}. Run embed_repo with force=true to re-embed.`,
    );
    this.name = 'DimensionMismatchError';
  }
}

/**
 * Raised when the configured embedding provider/model does not match the
 * one that built the index on disk. Querying a vector store cross-provider
 * silently returns garbage similarity scores — see CRG v2.3.3 security
 * hardening, which refuses to mix indexes built with different providers.
 *
 * The fix is one of:
 *   - switch the active provider config back to the one that built the index, or
 *   - run `embed_repo({ force: true })` to drop and re-embed under the new
 *     provider.
 */
export class ProviderMismatchError extends Error {
  constructor(
    public readonly stored: { provider: string; model: string },
    public readonly active: { provider: string; model: string },
  ) {
    super(
      `Embedding provider mismatch: index was built with ${stored.provider}/${stored.model}, ` +
        `current config uses ${active.provider}/${active.model}. ` +
        `Run embed_repo with force=true to re-embed under the new provider, ` +
        `or revert the AI provider config to match the index.`,
    );
    this.name = 'ProviderMismatchError';
  }
}

export type ProviderCheckResult =
  | { kind: 'ok' }
  | { kind: 'no_index' }
  | {
      kind: 'mismatch';
      stored: { provider: string; model: string };
      active: { provider: string; model: string };
    };

export class BlobVectorStore implements VectorStore {
  /** Cached expected dim — null until first getMeta() or setMeta(). */
  private cachedDim: number | null = null;
  /** Optional sqlite-vec ANN accelerator over the BLOB table; null when absent. */
  private vec: Vec0Index | null = null;

  constructor(private db: Database.Database) {
    this.ensureTable();
    const meta = this.getMeta();
    if (meta) this.cachedDim = meta.dim;
    this.vec = Vec0Index.tryCreate(db);
    this.syncVecIndex();
  }

  private ensureTable(): void {
    this.db.exec(CREATE_TABLE);
  }

  /**
   * Backfill the ANN accelerator from the canonical BLOB table if it is behind.
   *
   * GH#1447: the old check (`vec.count() >= count()` → in sync) was blind in
   * the orphan direction — deleted symbols inflate the vec count, so drift was
   * undetectable and orphan vectors kept eating ANN top-k slots. Reconciliation
   * now compares both directions (orphans pruned, missing backfilled).
   */
  private syncVecIndex(): void {
    if (!this.vec) return;
    const dim = this.cachedDim ?? this.peekDim();
    if (dim == null) return; // no vectors yet — vec0 is created lazily on first insert
    this.reconcileVecIndex(dim);
  }

  /**
   * Reconcile the vec0 accelerator against the canonical BLOB table.
   *
   * Public so repair tooling and tests can invoke it directly. Never throws —
   * the BLOB table stays canonical and search falls back to brute force.
   */
  reconcileVecIndex(dim?: number): { pruned: number; backfilled: number; rebuilt: boolean } {
    const result = { pruned: 0, backfilled: 0, rebuilt: false };
    if (!this.vec) return result;
    const d = dim ?? this.cachedDim ?? this.peekDim() ?? undefined;
    if (d == null) return result;
    const live = this.count();
    if (live === 0) {
      // No live vectors: any accelerator row is an orphan (e.g. every symbol
      // was deleted while vec0 survived). Drop the table so stale ids can't
      // pollute ANN top-k; it is re-created lazily on the next insert.
      if (this.vec.countOrphans() > 0) {
        this.vec.clear();
        result.rebuilt = true;
      }
      return result;
    }
    const orphans = this.vec.countOrphans();
    const missing = this.vec.countMissing();
    if (orphans === 0 && missing === 0) return result; // in sync, both directions
    if (shouldRebuildVecIndex(orphans, live)) {
      // Mass-delete bloat: row-by-row DELETEs leave dead weight in vec0
      // shadow chunks (GH#1447 measured 6–12x vs live data). DROP + full
      // backfill compacts them.
      this.vec.clear();
      result.rebuilt = true;
      result.pruned = orphans;
      result.backfilled = this.backfillAll(d);
      return result;
    }
    if (orphans > 0) result.pruned = this.vec.pruneOrphans();
    result.backfilled = missing < 0 ? this.backfillAll(d) : this.backfillMissing(d, live);
    return result;
  }

  /** Full refill from the canonical table (idempotent delete-then-insert). */
  private backfillAll(dim: number): number {
    // Read in chunks via all() (not iterate): better-sqlite3 forbids writing while a
    // read iterator is open on the same connection, and chunking bounds peak memory
    // for a large one-time backfill.
    const CHUNK = 1000;
    let inserted = 0;
    const read = this.db.prepare(
      'SELECT symbol_id, embedding FROM symbol_embeddings ORDER BY symbol_id LIMIT ? OFFSET ?',
    );
    for (let offset = 0; ; offset += CHUNK) {
      const rows = read.all(CHUNK, offset) as { symbol_id: number; embedding: Buffer }[];
      if (rows.length === 0) break;
      this.vec!.backfill(rows, dim);
      inserted += rows.length;
    }
    return inserted;
  }

  /**
   * Backfill only the BLOB rows missing from vec0. Pages `LIMIT n` without
   * OFFSET — the missing set shrinks as rows land, so OFFSET would skip rows.
   * Iterations are capped at one full pass over the live set: if vec0 refuses
   * rows (hiccup), the same set would return forever.
   */
  private backfillMissing(dim: number, live: number): number {
    const CHUNK = 1000;
    const maxIters = Math.ceil(live / CHUNK) + 1;
    let inserted = 0;
    for (let iter = 0; iter < maxIters; iter++) {
      let rows: { symbol_id: number; embedding: Buffer }[];
      try {
        rows = this.db
          .prepare(
            'SELECT symbol_id, embedding FROM symbol_embeddings ' +
              `WHERE symbol_id NOT IN (SELECT symbol_id FROM ${VEC_TABLE_NAME}) LIMIT ?`,
          )
          .all(CHUNK) as { symbol_id: number; embedding: Buffer }[];
      } catch {
        // vec0 refuses the membership scan — fall back to a full refill.
        return this.backfillAll(dim);
      }
      if (rows.length === 0) break;
      this.vec!.backfill(rows, dim);
      inserted += rows.length;
    }
    return inserted;
  }

  /** Infer vector dimensionality from a stored BLOB (bytes / 4 = float32 count). */
  private peekDim(): number | null {
    const row = this.db.prepare('SELECT embedding FROM symbol_embeddings LIMIT 1').get() as
      | { embedding: Buffer }
      | undefined;
    return row ? row.embedding.byteLength / 4 : null;
  }

  insert(id: number, vector: number[]): void {
    if (this.cachedDim !== null && vector.length !== this.cachedDim) {
      throw new DimensionMismatchError(this.cachedDim, vector.length);
    }
    const buf = Buffer.from(new Float32Array(vector).buffer);
    this.db
      .prepare('INSERT OR REPLACE INTO symbol_embeddings (symbol_id, embedding) VALUES (?, ?)')
      .run(id, buf);
    this.vec?.insert(id, buf, vector.length);
  }

  search(query: number[], limit: number): { id: number; score: number }[] {
    if (this.cachedDim !== null && query.length !== this.cachedDim) return [];

    const queryArr = new Float32Array(query);
    const queryNorm = vecNorm(queryArr);
    if (queryNorm === 0) return [];

    // Fast path: sub-linear ANN via sqlite-vec when the extension is loaded.
    // Returns null on any vec0 error → fall through to the brute-force scan.
    if (this.vec) {
      const ann = this.vec.search(Buffer.from(queryArr.buffer), limit, query.length);
      if (ann) return ann;
    }

    // Use iterate() instead of all() to avoid loading every embedding into memory at once.
    // Maintain a min-heap of top-K results so we only keep `limit` items in memory.
    const topK: { id: number; score: number }[] = [];
    let minScore = -Infinity;

    const stmt = this.db.prepare('SELECT symbol_id, embedding FROM symbol_embeddings');
    for (const row of stmt.iterate() as Iterable<{ symbol_id: number; embedding: Buffer }>) {
      const embedding = new Float32Array(
        row.embedding.buffer,
        row.embedding.byteOffset,
        row.embedding.byteLength / 4,
      );
      const sim = cosineSimilarity(queryArr, embedding, queryNorm);

      if (topK.length < limit) {
        topK.push({ id: row.symbol_id, score: sim });
        if (topK.length === limit) {
          topK.sort((a, b) => a.score - b.score);
          minScore = topK[0].score;
        }
      } else if (sim > minScore) {
        topK[0] = { id: row.symbol_id, score: sim };
        topK.sort((a, b) => a.score - b.score);
        minScore = topK[0].score;
      }
    }

    topK.sort((a, b) => b.score - a.score);
    return topK;
  }

  delete(id: number): void {
    this.db.prepare('DELETE FROM symbol_embeddings WHERE symbol_id = ?').run(id);
    this.vec?.delete(id);
  }

  count(): number {
    const row = this.db.prepare('SELECT COUNT(*) as cnt FROM symbol_embeddings').get() as {
      cnt: number;
    };
    return row.cnt;
  }

  clear(): void {
    this.db.exec('DELETE FROM symbol_embeddings');
    this.vec?.clear();
  }

  setMeta(model: string, dim: number, provider?: string): void {
    const upsert = this.db.prepare(
      'INSERT OR REPLACE INTO embedding_meta (key, value) VALUES (?, ?)',
    );
    const tx = this.db.transaction(() => {
      upsert.run('model', model);
      upsert.run('dim', String(dim));
      if (provider !== undefined) {
        upsert.run('provider', provider);
      }
    });
    tx();
    this.cachedDim = dim;
  }

  getMeta(): { model: string; dim: number; provider?: string } | null {
    const rows = this.db
      .prepare("SELECT key, value FROM embedding_meta WHERE key IN ('model', 'dim', 'provider')")
      .all() as { key: string; value: string }[];
    if (rows.length < 2) return null;
    const map = new Map(rows.map((r) => [r.key, r.value]));
    const model = map.get('model');
    const dimStr = map.get('dim');
    const provider = map.get('provider');
    if (model === undefined || dimStr === undefined) return null;
    const dim = parseInt(dimStr, 10);
    if (!Number.isFinite(dim) || dim <= 0) return null;
    return provider !== undefined ? { model, dim, provider } : { model, dim };
  }

  /**
   * Compare the (provider, model) the index was built with against the
   * currently-active provider/model and report mismatch.
   *
   * Older indexes that pre-date the provider column return `kind: 'ok'` —
   * we don't have the data to tell, and refusing every legacy index would
   * surprise users. The provider column gets backfilled the next time
   * embed_repo runs.
   */
  checkProviderMatch(active: { provider: string; model: string }): ProviderCheckResult {
    const stored = this.getMeta();
    if (!stored) return { kind: 'no_index' };
    // Legacy index without a provider column — accept and let setMeta
    // backfill on the next embed_repo run.
    if (stored.provider === undefined) return { kind: 'ok' };
    if (stored.provider === active.provider && stored.model === active.model) {
      return { kind: 'ok' };
    }
    return {
      kind: 'mismatch',
      stored: { provider: stored.provider, model: stored.model },
      active,
    };
  }
}

function vecNorm(v: Float32Array): number {
  let sum = 0;
  for (let i = 0; i < v.length; i++) {
    sum += v[i] * v[i];
  }
  return Math.sqrt(sum);
}

function cosineSimilarity(a: Float32Array, b: Float32Array, aNorm?: number): number {
  if (a.length !== b.length) return 0;

  let dot = 0;
  let bSumSq = 0;

  for (let i = 0; i < a.length; i++) {
    dot += a[i] * b[i];
    bSumSq += b[i] * b[i];
  }

  const normA = aNorm ?? vecNorm(a);
  const normB = Math.sqrt(bSumSq);

  if (normA === 0 || normB === 0) return 0;
  return dot / (normA * normB);
}
